import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { QrcodeSharingScreen } from '@euler/app/flows/common/QrcodeSharing';
import { VehicleSubscriptionsScreen } from '@euler/app/flows/common/VehicleSubscriptionsScreen';
import {
  getTaskResourceNamespace,
  TaskContextProvider,
} from '@euler/app/flows/task/functions/TaskContext';
import { DefaultTVLoader } from '@euler/app/tv/components/DefaultTVLoader';
import { ConstructionScreen } from '@euler/app/tv/pages/Construction';
import { InspectionScreen } from '@euler/app/tv/pages/Inspection';
import { MaintenancePlanScreen } from '@euler/app/tv/pages/MaintenancePlan';
import { MediasScreen } from '@euler/app/tv/pages/Medias';
import { PreInspectionScreen } from '@euler/app/tv/pages/PreInspection';
import { ServiceRecordsScreen } from '@euler/app/tv/pages/ServiceRecords';
import { SiteInspectionDetailScreen } from '@euler/app/tv/pages/SiteInspectionDetail';
import { TaskDetailScreen } from '@euler/app/tv/pages/TaskDetail';
import { TimelineScreen } from '@euler/app/tv/pages/Timeline';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { createStackNavigator } from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { MotiView } from 'moti';

const Stack = createStackNavigator();

const Screens = {
  TaskDetail: TaskDetailScreen,
  PreInspection: PreInspectionScreen,
  Inspection: InspectionScreen,
  Construction: ConstructionScreen,
  _qrcodeSharing: QrcodeSharingScreen,
  _vehicleSubscriptions: VehicleSubscriptionsScreen,
  _serviceRecords: ServiceRecordsScreen,
  _vehicleMedias: MediasScreen,
  _inspectedSiteDetail: SiteInspectionDetailScreen,
  _maintenancePlan: MaintenancePlanScreen,
  _timeline: TimelineScreen,
};

export type TaskNavParams = ScreenToParams<typeof Screens>;

type Props = {
  taskNo: string;
};

export const TaskFlow = wrapNavigatorScreen(
  (props: Props) => {
    const resourceNamespace = getTaskResourceNamespace(props.taskNo);
    return (
      <AppNavigationProvider>
        <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
          <Sentry.ErrorBoundary
            // @ts-ignore
            fallback={(params: any) => <ErrorFallback {...params} />}
          >
            <ScopedObservableSuspenseResourceContextProvider
              scope={resourceNamespace}
            >
              <TaskContextProvider
                taskNo={props.taskNo}
                suspenseFallback={<DefaultTVLoader />}
              >
                <MotiView
                  from={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ type: 'timing', duration: 500 }}
                  css={`
                    flex: 1;
                  `}
                >
                  <Stack.Navigator
                    initialRouteName={'TaskDetail'}
                    screenOptions={{
                      headerShown: false,
                      cardStyle: {},
                    }}
                  >
                    {spreadScreens(Stack.Screen, Screens, 'TaskDetail', props)}
                  </Stack.Navigator>
                </MotiView>
              </TaskContextProvider>
            </ScopedObservableSuspenseResourceContextProvider>
          </Sentry.ErrorBoundary>
        </LayoutProviderView>
      </AppNavigationProvider>
    );
  },
  { headerShown: false },
);
